import React from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PublishIcon from '@mui/icons-material/Publish';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useNavigate } from 'react-router';

import { paths } from '../../routes/paths';
import { colors } from '../../theme/tokens';
import { useTenantVersions } from '../../api/useTenantVersions';
import { useHasUnpublishedChanges } from '../../api/useHasUnpublishedChanges';
import { predictNextVersion } from '../../utils/semver';
import { useEnvOptional } from '../../context/EnvContext';
import VersionChip from '../../components/ui/VersionChip';
import EnvDropdown from './EnvDropdown';
import { usePublishGate } from '../../context/usePublishGate';
import { useCurrentUser } from '../../auth/useCurrentUser';

interface Props {
  tenantCode: string;
  isCompact: boolean;
  onOpenDrawer: () => void;
  onOpenSearch: () => void;
}

/**
 * Top bar with hamburger (compact only), search button, publish button,
 * and help/user-guide button. Kept as a separate component so the Layout
 * shell stays small and the top bar can be tested in isolation.
 */
const TopBar: React.FC<Props> = ({ tenantCode, isCompact, onOpenDrawer, onOpenSearch }) => {
  const navigate = useNavigate();
  // Per-tenant version (mig 219): show THIS tenant's version on the currently
  // selected env, next to the switcher — not a global env head. Defaults to
  // 1.0.0 on the home env when the tenant hasn't published; hidden when no
  // tenant is selected or the tenant isn't promoted to the selected env.
  const selectedEnv = useEnvOptional()?.currentEnv ?? '';
  const { rows: tenantVersionRows } = useTenantVersions(tenantCode || undefined);
  const selectedRow = tenantVersionRows.find((r) => r.env === selectedEnv);
  const tenantVersionStr = selectedRow
    ? selectedRow.versionStr ?? (selectedRow.isHome ? '1.0.0' : null)
    : null;
  // The env switcher is available to all users — per-env reachability is still
  // enforced inside EnvDropdown (non-readable envs are disabled).
  // Mute the Publish button when publish can't proceed (read-only here, or the
  // tenant's content readiness is blocked). It stays CLICKABLE so the user can
  // still open the Publish page to see/fix the block.
  const publishGate = usePublishGate(tenantCode);
  // RBAC_V2 `publish` permission gates the button itself (decisions §30) —
  // layered ON TOP of the read-only/content-readiness mute above, which
  // still applies to a holder. LEGACY_SHIM.publish is always true, so flag
  // off keeps this byte-identical (button always renders, as today).
  const { hasPermission } = useCurrentUser();
  const canPublish = hasPermission('publish');
  // Pending-version chip: on the home env, if there are unpublished edits, show
  // the version a publish WOULD mint (amber) instead of the current pointer.
  // Computed unconditionally (hook rules); the probe is gated to home only.
  const isHome = selectedRow?.isHome === true;
  const hasUnpublished = useHasUnpublishedChanges(tenantCode || undefined, isHome);
  const showPending = isHome && hasUnpublished && !!tenantVersionStr;
  const predictedVersion = tenantVersionStr ? predictNextVersion(tenantVersionStr) : null;
  return (
    <Box
      // ReadOnlyGuard exclusion: the top bar (env switcher, search, nav actions)
      // must stay interactive in a read-only env so the user can switch back to
      // the editable environment. See src/context/ReadOnlyGuard.tsx.
      data-readonly-interactive="true"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: isCompact ? 2 : 4,
        pt: 2,
        pb: 0,
        flexWrap: 'wrap',
      }}
    >
      {isCompact && (
        <IconButton
          onClick={onOpenDrawer}
          sx={{ mr: 'auto', color: colors.textPrimary }}
          aria-label="Open navigation"
        >
          <MenuIcon />
        </IconButton>
      )}

      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* WU11 — env switcher. Picks which env's data the page reads from
            (axiosClient interceptor swaps baseURL on every request). On
            non-editable envs the ReadOnlyBanner appears + every edit button
            disables via useIsEditable. Available to all users — per-env
            reachability is enforced inside EnvDropdown. */}
        <EnvDropdown isCompact={isCompact} />

        {/* Per-tenant version (mig 219) — THIS tenant's version on the selected env.
            On home with unpublished edits, show the predicted next version (amber).
            Shown to ALL roles (informational — not gated). */}
        {!isCompact && tenantCode && tenantVersionStr && (
          showPending ? (
            <VersionChip
              version={predictedVersion!}
              variant="pending"
              tooltip={`Unpublished changes — publish to mint v${predictedVersion}`}
            />
          ) : (
            <VersionChip
              version={tenantVersionStr}
              variant={selectedRow?.isHome ? 'home' : 'neutral'}
              tooltip={`${tenantCode} · ${selectedEnv.toUpperCase()}`}
            />
          )
        )}

        {!isCompact ? (
          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={onOpenSearch}
            sx={{
              textTransform: 'none',
              borderColor: colors.borderStrong,
              color: colors.textSecondary,
              fontFamily: 'Manrope, sans-serif',
              fontSize: 13,
              borderRadius: '8px',
              px: 2,
              height: 38,
            }}
          >
            Search...
            <Typography component="span" sx={{ fontSize: 11, color: colors.textTertiary, ml: 1 }}>
              Ctrl+K
            </Typography>
          </Button>
        ) : (
          <IconButton
            onClick={onOpenSearch}
            aria-label="Open search"
            sx={{ color: colors.textSecondary }}
          >
            <SearchIcon />
          </IconButton>
        )}

        {canPublish && (
          <Tooltip title={publishGate.reason} arrow>
            {/* Kept clickable even when muted — opens the Publish page so the user
                can see/fix the block. The color signals it isn't ready. */}
            <Button
              variant="contained"
              disableElevation
              startIcon={<PublishIcon />}
              onClick={() => navigate(paths.publish(tenantCode))}
              aria-label="Publish"
              data-publish-disabled={publishGate.disabled ? 'true' : 'false'}
              sx={{
                textTransform: 'none',
                backgroundColor: publishGate.disabled ? colors.bgMuted : colors.brand,
                color: publishGate.disabled ? colors.textTertiary : colors.textPrimary,
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 700,
                fontSize: 14,
                borderRadius: '8px',
                px: isCompact ? 2 : 3,
                minWidth: isCompact ? 0 : undefined,
                height: 38,
                border: publishGate.disabled ? `1px solid ${colors.borderDefault}` : 'none',
                '&:hover': {
                  backgroundColor: publishGate.disabled ? colors.borderLight : colors.brandHover,
                },
              }}
            >
              {!isCompact && 'Publish'}
            </Button>
          </Tooltip>
        )}

        <Button
          variant="outlined"
          onClick={() => navigate(paths.userGuide(tenantCode))}
          aria-label="User guide"
          sx={{
            textTransform: 'none',
            borderColor: colors.borderStrong,
            color: colors.textSecondary,
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 600,
            fontSize: 13,
            borderRadius: '8px',
            px: isCompact ? 1.5 : 2,
            height: 38,
            minWidth: 0,
            '&:hover': {
              borderColor: colors.brand,
              color: colors.textPrimary,
              backgroundColor: `${colors.brand}10`,
            },
          }}
        >
          <HelpOutlineIcon sx={{ width: 18, height: 18 }} />
        </Button>
      </Box>
    </Box>
  );
};

export default TopBar;
